'use client';

import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, Info, AlertTriangle, Users, Clock, TrendingUp } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export const KPI_THRESHOLDS_KEY = 'kpi_thresholds_config';

export interface KPIThresholdsConfig {
  unassignedDutyLimit: number;       // alert when unassigned duties exceed this
  crewAvailabilityPct: number;       // alert when crew availability % drops below this
  violationSpikeCount: number;       // number of violations that triggers a spike alert
  violationSpikeWindowMin: number;   // rolling window in minutes for spike detection
}

export const DEFAULT_KPI_THRESHOLDS: KPIThresholdsConfig = {
  unassignedDutyLimit: 5,
  crewAvailabilityPct: 70,
  violationSpikeCount: 3,
  violationSpikeWindowMin: 5,
};

export function loadKPIThresholds(): KPIThresholdsConfig {
  if (typeof window === 'undefined') return DEFAULT_KPI_THRESHOLDS;
  try {
    const raw = localStorage.getItem(KPI_THRESHOLDS_KEY);
    if (!raw) return DEFAULT_KPI_THRESHOLDS;
    return { ...DEFAULT_KPI_THRESHOLDS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_KPI_THRESHOLDS;
  }
}

function SaveBanner({ onSave, onReset }: { onSave: () => void; onReset: () => void }) {
  return (
    <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 mb-5">
      <div className="flex items-center gap-2 text-sm text-primary">
        <Info size={15} />
        <span>You have unsaved changes.</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onReset}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-500 text-muted-foreground hover:bg-muted transition-all"
        >
          <RotateCcw size={13} /> Reset
        </button>
        <button
          onClick={onSave}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-600 bg-primary text-primary-foreground hover:bg-primary/90 transition-all"
        >
          <Save size={13} /> Save Changes
        </button>
      </div>
    </div>
  );
}

interface ThresholdCardProps {
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  title: string;
  description: string;
  children: React.ReactNode;
}

function ThresholdCard({ icon: Icon, iconColor, iconBg, title, description, children }: ThresholdCardProps) {
  return (
    <div className="border border-border rounded-xl bg-card p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
          <Icon size={17} className={iconColor} />
        </div>
        <div>
          <h3 className="text-sm font-600 text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function KPIThresholdsPanel() {
  const [config, setConfig] = useState<KPIThresholdsConfig>(DEFAULT_KPI_THRESHOLDS);
  const [saved, setSaved] = useState<KPIThresholdsConfig>(DEFAULT_KPI_THRESHOLDS);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const loaded = loadKPIThresholds();
    setConfig(loaded);
    setSaved(loaded);
  }, []);

  const isDirty = JSON.stringify(config) !== JSON.stringify(saved);

  const handleSave = () => {
    localStorage.setItem(KPI_THRESHOLDS_KEY, JSON.stringify(config));
    setSaved(config);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  const handleReset = () => {
    setConfig(saved);
  };

  const handleRestoreDefaults = () => {
    setConfig(DEFAULT_KPI_THRESHOLDS);
  };

  const update = (field: keyof KPIThresholdsConfig, value: number) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-base font-600 text-foreground">KPI Alert Thresholds</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Configure the threshold values that trigger real-time KPI alerts. Adjust these to match your depot's operational norms.
        </p>
      </div>

      {isDirty && <SaveBanner onSave={handleSave} onReset={handleReset} />}

      {saveSuccess && (
        <div className="flex items-center gap-2 bg-positive/10 border border-positive/20 text-positive rounded-xl px-4 py-3 mb-5 text-sm">
          <Save size={14} />
          <span>Threshold settings saved. Alerts will use the new values immediately.</span>
        </div>
      )}

      <div className="space-y-4">
        {/* Unassigned Duty Limit */}
        <ThresholdCard
          icon={Clock}
          iconColor="text-amber-600"
          iconBg="bg-amber-50"
          title="Unassigned Duty Limit"
          description="Fire a warning alert when the number of unassigned duties today exceeds this value."
        >
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <input
                type="range"
                min={1}
                max={30}
                step={1}
                value={config.unassignedDutyLimit}
                onChange={(e) => update('unassignedDutyLimit', Number(e.target.value))}
                className="w-full accent-amber-500"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                <span>1</span>
                <span>30</span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <input
                type="number"
                min={1}
                max={30}
                value={config.unassignedDutyLimit}
                onChange={(e) => update('unassignedDutyLimit', Math.min(30, Math.max(1, Number(e.target.value))))}
                className="w-16 text-center text-sm font-700 border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <span className="text-xs text-muted-foreground">duties</span>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-100">
            <AlertTriangle size={13} className="text-amber-600 shrink-0" />
            <p className="text-xs text-amber-700">
              Alert fires when unassigned duties &gt; <strong>{config.unassignedDutyLimit}</strong>
            </p>
          </div>
        </ThresholdCard>

        {/* Crew Availability % */}
        <ThresholdCard
          icon={Users}
          iconColor="text-red-600"
          iconBg="bg-red-50"
          title="Crew Availability Threshold"
          description="Fire a critical alert when the percentage of available crew members drops below this value."
        >
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <input
                type="range"
                min={10}
                max={95}
                step={5}
                value={config.crewAvailabilityPct}
                onChange={(e) => update('crewAvailabilityPct', Number(e.target.value))}
                className="w-full accent-red-500"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                <span>10%</span>
                <span>95%</span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <input
                type="number"
                min={10}
                max={95}
                value={config.crewAvailabilityPct}
                onChange={(e) => update('crewAvailabilityPct', Math.min(95, Math.max(10, Number(e.target.value))))}
                className="w-16 text-center text-sm font-700 border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <span className="text-xs text-muted-foreground">%</span>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100">
            <AlertTriangle size={13} className="text-red-600 shrink-0" />
            <p className="text-xs text-red-700">
              Alert fires when crew availability &lt; <strong>{config.crewAvailabilityPct}%</strong>
            </p>
          </div>
        </ThresholdCard>

        {/* Violation Spike */}
        <ThresholdCard
          icon={TrendingUp}
          iconColor="text-purple-600"
          iconBg="bg-purple-50"
          title="Violation Spike Detection"
          description="Fire a spike alert when a set number of violations occur within a rolling time window."
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Spike count */}
            <div>
              <label className="text-xs font-500 text-muted-foreground block mb-2">
                Violations to trigger spike
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={2}
                  max={20}
                  step={1}
                  value={config.violationSpikeCount}
                  onChange={(e) => update('violationSpikeCount', Number(e.target.value))}
                  className="flex-1 accent-purple-500"
                />
                <div className="flex items-center gap-1.5 shrink-0">
                  <input
                    type="number"
                    min={2}
                    max={20}
                    value={config.violationSpikeCount}
                    onChange={(e) => update('violationSpikeCount', Math.min(20, Math.max(2, Number(e.target.value))))}
                    className="w-14 text-center text-sm font-700 border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <span className="text-xs text-muted-foreground">violations</span>
                </div>
              </div>
            </div>

            {/* Window */}
            <div>
              <label className="text-xs font-500 text-muted-foreground block mb-2">
                Rolling window (minutes)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={60}
                  step={1}
                  value={config.violationSpikeWindowMin}
                  onChange={(e) => update('violationSpikeWindowMin', Number(e.target.value))}
                  className="flex-1 accent-purple-500"
                />
                <div className="flex items-center gap-1.5 shrink-0">
                  <input
                    type="number"
                    min={1}
                    max={60}
                    value={config.violationSpikeWindowMin}
                    onChange={(e) => update('violationSpikeWindowMin', Math.min(60, Math.max(1, Number(e.target.value))))}
                    className="w-14 text-center text-sm font-700 border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <span className="text-xs text-muted-foreground">min</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 border border-purple-100">
            <AlertTriangle size={13} className="text-purple-600 shrink-0" />
            <p className="text-xs text-purple-700">
              Alert fires when ≥ <strong>{config.violationSpikeCount}</strong> violations occur within a <strong>{config.violationSpikeWindowMin}-minute</strong> window
            </p>
          </div>
        </ThresholdCard>
      </div>

      {/* Footer actions */}
      <div className="mt-6 flex items-center justify-between pt-4 border-t border-border">
        <button
          onClick={handleRestoreDefaults}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-500 text-muted-foreground hover:bg-muted transition-all border border-border"
        >
          <RotateCcw size={13} /> Restore Defaults
        </button>
        <button
          onClick={handleSave}
          disabled={!isDirty}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-600 bg-primary text-primary-foreground hover:bg-primary/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Save size={14} /> Save Thresholds
        </button>
      </div>
    </div>
  );
}
